import React, {useState} from 'react';
import Avatar from '@mui/material/Avatar';
import {Typography} from '@mui/material';
import AvatarGroup from '@mui/material/AvatarGroup';
import Stack from '@mui/material/Stack';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import {getColorByFirstName} from '../../../utils/utils';
import {AurLightTooltip} from '../aur-light-tooltip/aur-light-tooltip';

function getInitials(name: string, surname: string): string {
  return `${name.charAt(0)}${surname.charAt(0)}`.toUpperCase();
}

export interface User {
  name: string;
  surname: string;
  userIconLink?: string;
  color?: string;
}

interface AurGroupAvatarProps {
  users: User[];
  maxVisible?: number;
}

export default function AurGroupAvatar({
  users,
  maxVisible = 5 /* include "+n" elements avatar */,
}: AurGroupAvatarProps) {
  const visibleUsers = users.slice(0, maxVisible);
  const remainingUsers = users.length - maxVisible;
  const totalUsers = users.length;

  // Define a state to track which avatars failed to load
  const [loadError, setLoadError] = useState<number[]>([]);

  // Function to handle image loading errors
  const handleImageError = (index: number) => {
    setLoadError((prev) => [...prev, index]);
  };

  return (
    <Stack direction="row" spacing={2}>
      <div className="avatar-group">
        <AvatarGroup max={25} className={`typo-text label1_size`}>
          {visibleUsers.map((user: User, index: number) => (
            <Avatar
              key={index}
              className={`${
                !user.userIconLink || loadError.includes(index)
                  ? 'avatar-default-circle'
                  : 'avatar-loaded'
              }`}
              src={
                loadError.includes(index)
                  ? undefined
                  : user.userIconLink || undefined
              }
              alt={
                user.userIconLink
                  ? `${user.name} ${user.surname}s avatar`
                  : `${getInitials(user.name, user.surname)}`
              }
              onError={() => handleImageError(index)} // Handle image load error
              style={{
                backgroundColor:
                  !user.userIconLink || loadError.includes(index)
                    ? getColorByFirstName(user.name || user.surname || '') // Apply dynamic color
                    : undefined,
                zIndex: index, // Apply zIndex dynamically
              }}
            >
              {/* If no userIconLink or load error, render initials */}
              {(loadError.includes(index) || !user.userIconLink) &&
                user.name &&
                user.surname &&
                getInitials(user.name, user.surname)}

              {/* If no image, name, and surname, render the custom icon */}
              {(loadError.includes(index) || !user.userIconLink) &&
                !user.name &&
                !user.surname && (
                  <AccountCircleOutlinedIcon className="avatar-default-icon" />
                )}
            </Avatar>
          ))}
          {remainingUsers > 0 && (
            <AurLightTooltip title={`Total: ${totalUsers} users`}>
              <div>
                <Avatar className="avatar-plus-n">
                  <Typography
                    className={`typo-text profilePictureTextsmall_size PlusX-profile-picture-text-color`}
                  >
                    {`+${remainingUsers}`}
                  </Typography>
                </Avatar>
              </div>
            </AurLightTooltip>
          )}
        </AvatarGroup>
      </div>
    </Stack>
  );
}
